import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check } from "lucide-react";

const reasons = [
  "Verified Quality Assurance",
  "Quick Turnaround Times",
  "Competitive Pricing",
  "Dedicated Account Support",
  "Global Supplier Network",
  "End-to-End Service",
];

const stats = [
  { value: "500+", label: "Products Sourced" },
  { value: "100+", label: "Happy Clients" },
  { value: "15+", label: "Categories" },
  { value: "24/7", label: "Support" },
];

export function WhyUsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="why-us" className="py-32 bg-foreground text-background relative overflow-hidden">
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-px h-full bg-background" />
        <div className="absolute top-0 left-2/4 w-px h-full bg-background" />
        <div className="absolute top-0 left-3/4 w-px h-full bg-background" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left: Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-xs tracking-wide-elegant uppercase text-background/60 mb-6">
              Why Choose Us
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-background mb-8 leading-[1.1] font-medium">
              The Sailkha
              <br />
              <span className="text-background/60">Advantage</span>
            </h2>
            <p className="text-background/70 text-lg leading-relaxed mb-12">
              Partner with a company that prioritizes your success with unmatched
              service, quality, and reliability.
            </p>

            {/* Reasons list */}
            <div className="grid sm:grid-cols-2 gap-4">
              {reasons.map((reason, i) => (
                <motion.div
                  key={reason}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 rounded-full border border-background/40 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-background" />
                  </div>
                  <span className="text-background/80">{reason}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 gap-8"
          >
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                className="p-8 border border-background/10 text-center"
              >
                <div className="font-display text-5xl lg:text-6xl text-background mb-3">
                  {stat.value}
                </div>
                <div className="text-sm tracking-wide-elegant uppercase text-background/60">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
